import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sparkles, Send, Paperclip, Trash2, Plus, Zap, ScanLine,
  Loader2, FileText, X, Bot, User as UserIcon, Copy, Wand2,
  BatteryCharging, Sun, TrendingUp, Info,
} from "lucide-react";

const SUGGESTIONS = [
  "Suggest a solar + battery size for a 25,000 m² warehouse in Sydney with $18k monthly bills and 6am–6pm operations.",
  "Cold-storage site — 500,000 kWh/yr, three-phase, needs continuous refrigeration. What size and PPA rate makes sense?",
  "Childcare centre with 200 kWh/day, mostly daytime, roof space limited to ~120 m². What's realistic?",
  "How should we adjust sizing for a site that operates 24/7?",
];

export default function AdminGreenAdvisor() {
  const [params, setParams] = useSearchParams();
  const initialSid = params.get("sid") || null;
  const applicationId = params.get("application_id") || null;
  const [sessions, setSessions] = useState([]);
  const [currentSid, setCurrentSid] = useState(initialSid);
  const [session, setSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [attached, setAttached] = useState([]);
  const [tab, setTab] = useState("chat"); // 'chat' | 'analyse'
  const scrollRef = useRef(null);

  useEffect(() => { loadSessions(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (currentSid) loadSession(currentSid);
    else { setSession(null); setMessages([]); }
    // eslint-disable-next-line
  }, [currentSid]);

  useEffect(() => {
    // Auto-scroll to bottom when new message
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  useEffect(() => {
    // If URL has ?application_id=... and no sid yet, auto-create a session
    (async () => {
      if (applicationId && !currentSid) {
        try {
          const { data } = await api.post("/api/admin/green-advisor/sessions", { application_id: applicationId });
          setCurrentSid(data.id);
          setParams({ sid: data.id }, { replace: true });
          loadSessions();
        } catch (e) {
          toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
      }
    })();
    // eslint-disable-next-line
  }, [applicationId]);

  async function loadSessions() {
    try {
      const { data } = await api.get("/api/admin/green-advisor/sessions", { params: { limit: 50 } });
      setSessions(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }
  async function loadSession(sid) {
    try {
      const { data } = await api.get(`/api/admin/green-advisor/sessions/${sid}`);
      setSession(data.session);
      setMessages(data.messages || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }

  async function newSession(title) {
    try {
      const { data } = await api.post("/api/admin/green-advisor/sessions", { title: title || undefined });
      setCurrentSid(data.id);
      setParams({ sid: data.id }, { replace: true });
      await loadSessions();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }
  async function deleteSession(sid) {
    if (!window.confirm("Delete this advisor session?")) return;
    try {
      await api.delete(`/api/admin/green-advisor/sessions/${sid}`);
      if (currentSid === sid) { setCurrentSid(null); setParams({}, { replace: true }); }
      await loadSessions();
      toast.success("Session deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }

  async function handleAttach(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("category", "advisor_bill");
        const { data } = await api.post("/api/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAttached((prev) => [...prev, data]);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  async function sendMessage() {
    const content = text.trim();
    if (!content && attached.length === 0) return;
    let sid = currentSid;
    if (!sid) {
      // Auto-create session
      try {
        const { data } = await api.post("/api/admin/green-advisor/sessions", {});
        sid = data.id;
        setCurrentSid(sid);
        setParams({ sid }, { replace: true });
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
        return;
      }
    }
    const payload = {
      content: content || "(please analyse the attached documents)",
      file_ids: attached.map((a) => a.file_id),
    };
    setText("");
    // Optimistic user message
    const tempMsg = { id: `tmp-${Date.now()}`, role: "user", content: payload.content, file_ids: payload.file_ids, created_at: new Date().toISOString() };
    setMessages((m) => [...m, tempMsg]);
    setAttached([]);
    setBusy(true);
    try {
      const { data } = await api.post(`/api/admin/green-advisor/sessions/${sid}/messages`, payload);
      setMessages((m) => [...m.filter((x) => x.id !== tempMsg.id), data.user_message, data.assistant_message]);
      loadSessions();
    } catch (e) {
      const detail = formatApiError(e.response?.data?.detail) || e.message;
      toast.error(detail);
      setMessages((m) => m.filter((x) => x.id !== tempMsg.id));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="admin-green-advisor">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="text-forest text-xs tracking-[0.18em] uppercase font-semibold mb-2 flex items-center gap-2">
            <Sparkles size={13} className="text-bright"/> Green Advisor
          </p>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold text-forest">Sizing advice for renewable-energy experts</h1>
          <p className="text-neutral-500 text-[13.5px] mt-1 max-w-2xl">Ask the AI advisor for indicative solar + battery sizing, PPA rate ranges and quick electricity-bill analysis. Powered by Claude Sonnet 4.5.</p>
        </div>
        <div className="flex items-center gap-2 rounded-full bg-white border border-black/5 p-1">
          <TabBtn active={tab === "chat"} onClick={() => setTab("chat")} testId="ga-tab-chat">
            <Bot size={14} /> Chat
          </TabBtn>
          <TabBtn active={tab === "analyse"} onClick={() => setTab("analyse")} testId="ga-tab-analyse">
            <ScanLine size={14} /> Bill analysis
          </TabBtn>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[280px_1fr]">
        {/* Sidebar sessions */}
        <aside className="rounded-2xl bg-white border border-black/5 p-4 h-fit lg:sticky lg:top-6" data-testid="ga-sessions">
          <Button onClick={() => newSession()} data-testid="ga-new-session" className="w-full rounded-full h-10 bg-forest hover:bg-forest/90 text-white font-semibold">
            <Plus size={14} className="mr-1.5"/> New session
          </Button>
          <div className="mt-4 space-y-1 max-h-[520px] overflow-auto">
            {sessions.length === 0 && (
              <p className="text-[13px] text-neutral-500 px-1 py-4 text-center">No sessions yet.</p>
            )}
            {sessions.map((s) => {
              const active = s.id === currentSid;
              return (
                <div key={s.id} className={`group flex items-start gap-2 rounded-lg px-2.5 py-2 text-[13.5px] cursor-pointer ${active ? "bg-mist text-forest" : "hover:bg-mist/60 text-neutral-700"}`}>
                  <button
                    className="flex-1 text-left min-w-0"
                    onClick={() => { setCurrentSid(s.id); setParams({ sid: s.id }, { replace: true }); }}
                    data-testid={`ga-open-${s.id}`}
                  >
                    <p className={`truncate ${active ? "font-semibold" : "font-medium"}`}>{s.title || "Untitled"}</p>
                    <p className="text-[11px] text-neutral-500 truncate">{(s.updated_at || "").slice(0, 16).replace("T", " ")}</p>
                  </button>
                  <button
                    onClick={() => deleteSession(s.id)}
                    className="opacity-0 group-hover:opacity-100 text-neutral-400 hover:text-red-600 p-1"
                    data-testid={`ga-delete-${s.id}`}
                    aria-label="Delete session"
                  >
                    <Trash2 size={13}/>
                  </button>
                </div>
              );
            })}
          </div>
        </aside>

        {/* Main pane */}
        <section>
          {tab === "chat" && (
            <ChatPane
              session={session}
              messages={messages}
              busy={busy}
              text={text}
              setText={setText}
              attached={attached}
              setAttached={setAttached}
              uploading={uploading}
              handleAttach={handleAttach}
              sendMessage={sendMessage}
              scrollRef={scrollRef}
            />
          )}
          {tab === "analyse" && <AnalysePane />}
        </section>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, testId, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`inline-flex items-center gap-1.5 rounded-full h-9 px-4 text-[13px] font-semibold transition-colors ${active ? "bg-forest text-white" : "text-neutral-600 hover:text-forest"}`}
    >
      {children}
    </button>
  );
}

/* ---------- CHAT ---------- */
function ChatPane({ session, messages, busy, text, setText, attached, setAttached, uploading, handleAttach, sendMessage, scrollRef }) {
  const empty = messages.length === 0;
  return (
    <div className="rounded-2xl bg-white border border-black/5 flex flex-col min-h-[560px]" data-testid="ga-chat-pane">
      <div className="p-5 md:p-6 border-b border-black/5">
        <p className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold">{session?.title || "New session"}</p>
        <p className="text-[13px] text-neutral-600 mt-0.5">
          {session?.application_id ? "Session linked to an application — the customer's context is included automatically." : "General sizing conversation. You can attach PDF bills or CSV interval data."}
        </p>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-auto p-5 md:p-6 space-y-4" data-testid="ga-messages">
        {empty && !busy && (
          <div className="grid gap-3 max-w-2xl mx-auto pt-6">
            <p className="text-center text-[13px] text-neutral-500">Ask a question or start with a suggestion:</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {SUGGESTIONS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setText(s)}
                  data-testid={`ga-suggestion-${i}`}
                  className="text-left rounded-xl border border-black/5 bg-mist/60 hover:bg-mist p-3.5 text-[13.5px] text-forest leading-snug"
                >
                  <Wand2 size={14} className="inline mr-1.5 text-bright"/> {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) => (
          <MessageBubble key={m.id} m={m} />
        ))}
        {busy && <TypingBubble />}
      </div>

      <div className="border-t border-black/5 p-3 md:p-4">
        {attached.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2">
            {attached.map((a) => (
              <span key={a.file_id} className="inline-flex items-center gap-1.5 bg-mist border border-black/5 rounded-full px-2.5 py-1 text-[12px] text-forest">
                <FileText size={12}/> {a.original_filename}
                <button onClick={() => setAttached((prev) => prev.filter((x) => x.file_id !== a.file_id))} aria-label="Remove attachment">
                  <X size={11}/>
                </button>
              </span>
            ))}
          </div>
        )}
        <div className="flex gap-2 items-end">
          <label className={`h-11 w-11 rounded-full grid place-items-center border border-black/10 cursor-pointer bg-white text-forest hover:bg-mist ${uploading ? "opacity-60" : ""}`}>
            {uploading ? <Loader2 size={16} className="animate-spin"/> : <Paperclip size={16}/>}
            <input type="file" multiple className="hidden" accept=".pdf,.csv,.xlsx,.xls,.jpg,.jpeg,.png" onChange={handleAttach} data-testid="ga-attach"/>
          </label>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder="Ask about sizing, PPA rates, load profiles, battery configuration..."
            data-testid="ga-input"
            className="min-h-[44px] max-h-[160px] resize-none rounded-2xl"
            rows={1}
          />
          <Button
            onClick={sendMessage}
            disabled={busy || (!text.trim() && attached.length === 0)}
            data-testid="ga-send"
            className="rounded-full h-11 px-5 bg-bright hover:bg-bright/90 text-[hsl(150_40%_8%)] font-semibold"
          >
            {busy ? <Loader2 size={15} className="animate-spin"/> : <><Send size={14} className="mr-1"/>Send</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ m }) {
  const isUser = m.role === "user";
  return (
    <div className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`} data-testid={`ga-msg-${m.role}`}>
      <div className={`h-8 w-8 grid place-items-center rounded-full shrink-0 ${isUser ? "bg-mist text-forest" : "bg-forest text-white"}`}>
        {isUser ? <UserIcon size={14}/> : <Bot size={14}/>}
      </div>
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-[14px] leading-relaxed whitespace-pre-wrap ${isUser ? "bg-forest text-white" : "bg-mist text-forest"}`}>
        {m.content}
        {(m.file_ids || []).length > 0 && (
          <div className={`mt-2 pt-2 border-t ${isUser ? "border-white/15" : "border-black/5"} flex flex-wrap gap-1.5 text-[11.5px]`}>
            {(m.file_ids || []).map((fid) => (
              <span key={fid} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 ${isUser ? "bg-white/10" : "bg-white"}`}>
                <FileText size={10}/> file
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
function TypingBubble() {
  return (
    <div className="flex gap-3" data-testid="ga-typing">
      <div className="h-8 w-8 grid place-items-center rounded-full shrink-0 bg-forest text-white"><Bot size={14}/></div>
      <div className="rounded-2xl px-4 py-3 bg-mist text-forest">
        <span className="inline-flex gap-1 items-center">
          <span className="h-1.5 w-1.5 rounded-full bg-forest/60 animate-pulse"/>
          <span className="h-1.5 w-1.5 rounded-full bg-forest/60 animate-pulse" style={{ animationDelay: "0.15s" }}/>
          <span className="h-1.5 w-1.5 rounded-full bg-forest/60 animate-pulse" style={{ animationDelay: "0.3s" }}/>
        </span>
      </div>
    </div>
  );
}

/* ---------- BILL ANALYSIS ---------- */
function AnalysePane() {
  const [files, setFiles] = useState([]);
  const [ctx, setCtx] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function handleUpload(e) {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true);
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("category", "advisor_bill");
        const { data } = await api.post("/api/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFiles((p) => [...p, data]);
      }
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  async function analyse() {
    if (files.length === 0) return toast.error("Please upload at least one bill (PDF or CSV).");
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/api/admin/green-advisor/analyse-bill", {
        file_ids: files.map((f) => f.file_id),
        customer_context: ctx,
      });
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_1fr]" data-testid="ga-analyse-pane">
      <div className="rounded-2xl bg-white border border-black/5 p-5 md:p-6">
        <h3 className="font-heading font-semibold text-forest">Upload electricity bill(s)</h3>
        <p className="text-[13px] text-neutral-500 mt-1">PDF, CSV or XLSX. Multiple bills improve accuracy. The advisor will infer usage, tariff and recommend indicative system sizing + a PPA rate range.</p>

        <label className="mt-4 block rounded-2xl border-2 border-dashed border-black/10 hover:border-forest/40 hover:bg-mist/60 transition-colors p-5 cursor-pointer bg-white">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-xl bg-mist grid place-items-center text-forest">
              {uploading ? <Loader2 size={16} className="animate-spin"/> : <Paperclip size={16}/>}
            </span>
            <div className="flex-1">
              <p className="text-[13.5px] font-semibold text-forest">Click to upload bills</p>
              <p className="text-[11.5px] text-neutral-500 mt-0.5">PDF · CSV · XLSX</p>
            </div>
          </div>
          <input type="file" multiple className="hidden" accept=".pdf,.csv,.xlsx,.xls" onChange={handleUpload} data-testid="ga-analyse-upload"/>
        </label>

        {files.length > 0 && (
          <ul className="mt-3 space-y-1.5" data-testid="ga-analyse-files">
            {files.map((f) => (
              <li key={f.file_id} className="flex items-center gap-2 bg-mist border border-black/5 rounded-lg p-2.5">
                <FileText size={14} className="text-forest"/>
                <span className="text-[13px] flex-1 truncate">{f.original_filename}</span>
                <button onClick={() => setFiles((p) => p.filter((x) => x.file_id !== f.file_id))} aria-label="Remove"><X size={13}/></button>
              </li>
            ))}
          </ul>
        )}

        <div className="mt-5">
          <label className="block text-[13px] font-semibold text-forest">Extra context (optional)</label>
          <Textarea
            value={ctx}
            onChange={(e) => setCtx(e.target.value)}
            rows={3}
            data-testid="ga-analyse-context"
            placeholder="e.g. Cold-storage warehouse in Sydney, 24/7 refrigeration, roof metal ~1500m² usable, three-phase, wants backup."
            className="mt-1.5"
          />
        </div>

        <Button
          onClick={analyse}
          disabled={busy || uploading || files.length === 0}
          data-testid="ga-analyse-run"
          className="mt-5 w-full rounded-full h-11 bg-bright hover:bg-bright/90 text-[hsl(150_40%_8%)] font-semibold"
        >
          {busy ? <><Loader2 size={15} className="animate-spin mr-1.5"/> Analysing…</> : <><ScanLine size={15} className="mr-1.5"/> Run analysis</>}
        </Button>
      </div>

      <div className="rounded-2xl bg-mist border border-black/5 p-5 md:p-6 min-h-[400px]">
        <h3 className="font-heading font-semibold text-forest">Advisor recommendation</h3>
        <p className="text-[13px] text-neutral-500 mt-1">Indicative only — subject to detailed site assessment.</p>

        {!result && !busy && (
          <div className="mt-8 text-center text-neutral-500 text-[13.5px]" data-testid="ga-analyse-empty">
            <Sparkles size={22} className="mx-auto text-bright mb-2"/>
            Upload bill(s) and click &quot;Run analysis&quot; to get a recommendation.
          </div>
        )}
        {busy && <div className="mt-8 text-center text-neutral-500"><Loader2 size={20} className="mx-auto animate-spin"/></div>}
        {result && <AnalyseResult r={result}/>}
      </div>
    </div>
  );
}

function AnalyseResult({ r }) {
  if (r.error) {
    return (
      <div className="mt-4 rounded-xl bg-white border border-amber-200 p-4 text-[13.5px] text-amber-900" data-testid="ga-analyse-error">
        <Info size={16} className="inline mr-1.5"/> {r.error}
      </div>
    );
  }
  const ex = r.extracted || {};
  const rec = r.recommendation || {};
  return (
    <div className="mt-4 space-y-4" data-testid="ga-analyse-result">
      <div className="grid sm:grid-cols-2 gap-3">
        <Metric icon={Sun} label="Solar recommendation" value={`${rec.solar_kw_low}–${rec.solar_kw_high} kW`} tone="bright"/>
        <Metric icon={BatteryCharging} label="Battery recommendation" value={`${rec.battery_kwh_low}–${rec.battery_kwh_high} kWh`}/>
        <Metric icon={Zap} label="Indicative PPA rate" value={`${rec.ppa_rate_cents_low}–${rec.ppa_rate_cents_high} c/kWh`}/>
        <Metric icon={TrendingUp} label="Est. annual savings" value={`$${(rec.estimated_annual_savings_low_aud || 0).toLocaleString()}–$${(rec.estimated_annual_savings_high_aud || 0).toLocaleString()}`}/>
      </div>

      <Row label="Battery recommended" value={rec.battery_recommended ? "Yes" : "No"}/>
      <Row label="Recommended PPA term" value={rec.recommended_ppa_term_years ? `${rec.recommended_ppa_term_years} years` : "—"}/>
      <Row label="Confidence" value={<span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold ${rec.confidence === "high" ? "bg-emerald-100 text-emerald-800" : rec.confidence === "medium" ? "bg-amber-100 text-amber-800" : "bg-neutral-200 text-neutral-700"}`}>{(rec.confidence || "low").toUpperCase()}</span>}/>

      {rec.reasoning && (
        <div className="rounded-xl bg-white border border-black/5 p-4">
          <p className="text-[12px] uppercase tracking-widest font-semibold text-forest">Reasoning</p>
          <p className="mt-2 text-[13.5px] text-neutral-700 leading-relaxed whitespace-pre-wrap">{rec.reasoning}</p>
        </div>
      )}

      {(rec.risks_and_caveats || []).length > 0 && (
        <div className="rounded-xl bg-white border border-black/5 p-4">
          <p className="text-[12px] uppercase tracking-widest font-semibold text-forest">Risks & caveats</p>
          <ul className="mt-2 space-y-1 list-disc pl-5 text-[13.5px] text-neutral-700">
            {rec.risks_and_caveats.map((x, i) => <li key={i}>{x}</li>)}
          </ul>
        </div>
      )}

      {(rec.site_information_gaps || []).length > 0 && (
        <div className="rounded-xl bg-white border border-black/5 p-4">
          <p className="text-[12px] uppercase tracking-widest font-semibold text-forest">Info that would sharpen this</p>
          <ul className="mt-2 space-y-1 list-disc pl-5 text-[13.5px] text-neutral-700">
            {rec.site_information_gaps.map((x, i) => <li key={i}>{x}</li>)}
          </ul>
        </div>
      )}

      <details className="rounded-xl bg-white border border-black/5 p-4">
        <summary className="text-[12px] uppercase tracking-widest font-semibold text-forest cursor-pointer">Extracted from bill(s)</summary>
        <div className="mt-3 grid sm:grid-cols-2 gap-x-4 gap-y-1 text-[13px]">
          <Kv k="Retailer" v={ex.retailer}/>
          <Kv k="NMI" v={ex.nmi}/>
          <Kv k="Billing period" v={ex.billing_period}/>
          <Kv k="Monthly bill (AUD)" v={ex.monthly_bill_aud}/>
          <Kv k="Annual kWh (est)" v={ex.annual_kwh_estimate}/>
          <Kv k="Peak demand (kW)" v={ex.peak_demand_kw}/>
          <Kv k="Grid rate (c/kWh)" v={ex.grid_rate_cents_per_kwh_estimate}/>
          <Kv k="Tariff" v={ex.tariff_type}/>
        </div>
        {ex.notes && <p className="mt-3 text-[13px] text-neutral-600">{ex.notes}</p>}
      </details>

      <Button
        onClick={() => navigator.clipboard.writeText(JSON.stringify(r, null, 2)).then(() => toast.success("Copied JSON"))}
        variant="outline"
        className="rounded-full h-10 border-forest/20 text-forest hover:bg-white"
        data-testid="ga-analyse-copy"
      >
        <Copy size={14} className="mr-1.5"/> Copy JSON
      </Button>
    </div>
  );
}
function Metric({ icon: Icon, label, value, tone }) {
  const bright = tone === "bright";
  return (
    <div className={`rounded-xl p-4 ${bright ? "bg-bright/15 border border-bright/30" : "bg-white border border-black/5"}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-widest font-semibold text-forest/70">{label}</span>
        <Icon size={15} className="text-forest"/>
      </div>
      <p className="mt-1.5 font-heading font-semibold text-forest text-[18px]">{value}</p>
    </div>
  );
}
function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl bg-white border border-black/5 px-4 py-2.5">
      <span className="text-[13px] text-neutral-600">{label}</span>
      <span className="text-[13.5px] font-semibold text-forest text-right">{value ?? "—"}</span>
    </div>
  );
}
function Kv({ k, v }) {
  return (
    <div className="flex items-center justify-between border-b border-black/5 py-1">
      <span className="text-neutral-500">{k}</span>
      <span className="font-semibold text-forest text-right">{v ?? "—"}</span>
    </div>
  );
}
